/**
 * Unified authentication hook for the local auth provider.
 */

import { useCallback, useMemo } from 'react';

import { useLocalAuth } from '../components/LocalAuthProvider/LocalAuthProvider';

export interface AuthUser {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  nickname?: string;
  picture?: string;
}

export interface UseAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  error: Error | null;
  getAccessTokenSilently: () => Promise<string>;
  logout: (options?: { returnTo?: string }) => void;
  loginWithRedirect: () => void;
  refreshUser: () => Promise<void>;
}

/**
 * Hook for local authentication (BetterAuth)
 */
function useLocalAuthHook(): UseAuthResult {
  const {
    isAuthenticated,
    isLoading,
    user,
    error,
    getAccessTokenSilently,
    logout: localLogout,
    refreshUser,
  } = useLocalAuth();

  const normalizedUser = useMemo<AuthUser | null>(
    () =>
      user
        ? {
            sub: user.sub,
            email: user.email,
            email_verified: user.email_verified,
            name: user.name,
            given_name: user.given_name,
            family_name: user.family_name,
            nickname: user.preferred_username,
            picture: user.picture,
          }
        : null,
    [user],
  );

  const logout = useCallback(
    (_options?: { returnTo?: string }) => {
      localLogout({ returnTo: '/login' });
    },
    [localLogout],
  );

  const loginWithRedirect = useCallback(() => {
    window.location.href =
      '/login?returnTo=' + encodeURIComponent(window.location.pathname + window.location.search);
  }, []);

  return {
    isAuthenticated,
    isLoading,
    user: normalizedUser,
    error,
    getAccessTokenSilently,
    logout,
    loginWithRedirect,
    refreshUser,
  };
}

/**
 * Unified authentication hook
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, user, logout, getAccessTokenSilently } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <p>Please log in</p>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user?.name}!</p>
 *       <button onClick={() => logout()}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth(): UseAuthResult {
  return useLocalAuthHook();
}

export default useAuth;
